import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import NetworkManager from "../../app/managers/networkManager";
import { config } from "../../app/utils/config";
import { store } from "../../app/store/store";
import { setIsLoading } from "../common/commonSlice";

/**
 * V-Learning — the e-learning platform.
 *
 * ── What takes the global loader, and what does not ──────────────────────────────────
 *
 * `setIsLoading(true)` puts a full-screen spinner over the whole application. Only genuinely
 * blocking, user-initiated navigations take it: opening a course, starting a quiz attempt,
 * submitting one, and the two file exports.
 *
 * Everything else reports through its own local flag, because a spinner over the entire app
 * is the wrong feedback for it:
 *   - the progress heartbeat fires every few seconds while a video plays
 *   - the catalogue re-queries on every keystroke of the search box
 *   - the editor autosaves
 * A full-screen spinner on any of those would make the module unusable and would steal focus
 * from whatever the user was doing.
 *
 * ── The reach object ─────────────────────────────────────────────────────────────────
 *
 * `state.vlearn.reach` mirrors the backend's own answer to "how far does this user reach",
 * taken from the response rather than recomputed from the permissions array on the client.
 * The server already decided it when it filtered the rows; having the UI derive the same
 * conclusion a second time is how the two drift apart. It only ever decides which controls
 * are worth drawing — the server authorises every call again regardless.
 *
 * The same applies per course: `state.vlearn.access` is the five flags the backend returned
 * for the open course, not something the client works out from the manager list.
 */

/** The lesson types, in the order the authoring UI offers them. */
export const VLEARN_LESSON_TYPES = [
    'video_youtube', 'video_upload', 'pdf', 'slides', 'document', 'text', 'link', 'quiz'
]

/**
 * Which `vlearn_lesson_files.kind` each lesson type accepts.
 *
 * Must stay in sync with `LESSON_FILE_KINDS` in the backend's `helpers/vlearn.js`, which
 * rejects any other pairing — a kind added here and not there is a 422 the user cannot act on.
 */
export const VLEARN_LESSON_FILE_KINDS = {
    video_upload: ['video', 'caption'],
    pdf: ['pdf'],
    // A slides lesson holds up to two files: the .pptx the author wrote (kept downloadable so
    // the editable original is not lost) and the .pdf export, which is what actually renders
    // inline. No browser can display a .pptx without a conversion service.
    slides: ['pdf', 'slides_source'],
    document: ['document']
}

/**
 * Status pill colours, as VPlatform brand tokens rather than MUI palette names, so a pill
 * here reads the same as one anywhere else in the app. Follows `getTagClass()` in
 * app/utils/helpers.js: grey for not started, orange for in progress, green for done.
 */
export const VLEARN_STATUS_COLORS = {
    not_started: { backgroundColor: 'var(--lightGrey)', color: '#3a3a3a' },
    in_progress: { backgroundColor: 'var(--orange)', color: '#fff' },
    completed: { backgroundColor: 'var(--green)', color: '#fff' }
}

const initialState = {
    // ── Module configuration ────────────────────────────────────────────────────────
    settings: {
        video_source: 'both',
        max_video_upload_mb: 500,
        max_document_upload_mb: 50,
        activity_log_retention_days: 365,
        certificates_enabled: true,
        announcements_email_enabled: true
    },
    allowedLessonTypes: VLEARN_LESSON_TYPES,
    /** The backend's own conclusion about this user. Never derived on the client. */
    reach: { isAdmin: false, editAll: false, canCreate: false, hasTeamReach: false },
    categories: [],

    // ── Catalogue ───────────────────────────────────────────────────────────────────
    catalog: [],
    catalogTotal: 0,
    catalogSummary: { in_progress: 0, completed: 0, overdue: 0 },
    catalogLoading: false,

    // ── The open course ─────────────────────────────────────────────────────────────
    course: null,
    sections: [],
    managers: [],
    /** { canEdit, canViewDashboard, canViewCatalog, canLearn, isManager, isOwner, isEnrolled } */
    access: {
        canEdit: false, canViewDashboard: false, canViewCatalog: false, canLearn: false,
        isManager: false, isOwner: false, isEnrolled: false
    },
    enrollment: null,
    /** The lesson open in the player. Null = the course overview is showing. */
    currentLessonId: null,

    // ── Audience editor ─────────────────────────────────────────────────────────────
    /*
     * `audienceLoaded` is what tells a consumer this is REAL DATA rather than the placeholder.
     *
     * The picker used to hydrate its radio group from `audience` as soon as it was truthy —
     * and this default object always is. `closeCourse` resets to it, so on re-entering a
     * course the picker read `target_type: 'teams'` from the placeholder, latched (it hydrates
     * once per course, so as not to discard the author's edits), and then ignored the real
     * response when it landed. A course saved as "Mixed" came back as "By team".
     */
    audience: { target_type: 'teams', teams: [], collabs: [], enrolledCount: 0 },
    audienceLoaded: false,
    /** What this author is ALLOWED to target — straight from the server, never guessed. */
    audienceTargets: { teams: [], collabs: [], canTargetAll: false },
    managerCandidates: [],

    // ── Teacher dashboard ───────────────────────────────────────────────────────────
    dashboardSummary: null,
    dashboardLearners: [],
    dashboardLearnersTotal: 0,
    dashboardLessons: [],
    dashboardActivity: [],
    collabDetail: null,
    dashboardLoading: false,

    // ── Quizzes ─────────────────────────────────────────────────────────────────────
    quizzes: [],
    quizQuestions: [],
    /** The learner-facing view of one quiz: metadata + attempt history, never questions. */
    quizForLearner: null,
    /** The live attempt: { id_attempt, expires_at, questions: [...] }. No answer key. */
    attempt: null,
    attemptResult: null,
    attemptReview: null,
    myAttempts: [],

    // ── Announcements & my learning ─────────────────────────────────────────────────
    announcements: [],

    // ── Editor autosave, mirroring V-Resume's inline indicator ──────────────────────
    saveState: 'idle',
    uploadProgress: null
}

export const VLearnSlice = createSlice({
    name: "vlearn",
    initialState,
    reducers: {
        /**
         * Clears the open course.
         *
         * Everything course-scoped is reset together, deliberately: leaving `sections` or
         * `attemptReview` behind means the next course briefly renders the previous one's
         * curriculum before its own response lands.
         */
        closeCourse: (state) => {
            state.course = null
            state.sections = []
            state.managers = []
            state.access = initialState.access
            state.enrollment = null
            state.currentLessonId = null
            state.audience = initialState.audience
            state.audienceLoaded = false
            state.announcements = []
            state.quizzes = []
            state.quizQuestions = []
            state.quizForLearner = null
            state.attempt = null
            state.attemptResult = null
            state.attemptReview = null
            state.myAttempts = []
            state.dashboardSummary = null
            state.dashboardLearners = []
            state.dashboardLessons = []
            state.dashboardActivity = []
            state.collabDetail = null
            state.saveState = 'idle'
        },
        setCurrentLesson: (state, action) => {
            state.currentLessonId = action.payload
        },
        /**
         * Marks a lesson complete locally the moment the server confirms it.
         *
         * The alternative — refetching the whole course — would rebuild the sidebar and lose
         * the player's scroll position every time someone finishes a two-minute video.
         */
        patchLessonProgressLocal: (state, action) => {
            const { idLesson, changes } = action.payload
            state.sections = state.sections.map(section => ({
                ...section,
                // `(section.lessons || [])` — every other consumer in the module guards this
                // shape, so the response is treated as able to omit the key. Unguarded here it
                // would throw inside Immer on "mark as complete", which is an uncaught error
                // out of dispatch: a white screen mid-course.
                lessons: (section.lessons || []).map(lesson =>
                    lesson.id === idLesson ? { ...lesson, ...changes } : lesson)
            }))
        },
        /** Applies a curriculum reorder locally so a dragged row stays where it was dropped. */
        reorderSectionsLocal: (state, action) => {
            state.sections = action.payload
        },
        reorderLessonsLocal: (state, action) => {
            const { idSection, lessons } = action.payload
            state.sections = state.sections.map(section =>
                section.id === idSection ? { ...section, lessons } : section)
        },
        setSaveState: (state, action) => {
            state.saveState = action.payload
        },
        setUploadProgress: (state, action) => {
            state.uploadProgress = action.payload
        },
        /**
         * Discards the finished attempt so the quiz screen returns to its start state.
         * Kept separate from `closeCourse` because retaking a quiz must not close the course.
         */
        clearAttempt: (state) => {
            state.attempt = null
            state.attemptResult = null
        }
    },
    extraReducers: (builder) => {
        // Every reducer below null-guards `action.payload && action.payload.responseData`,
        // because the thunks in this file never reject — they return the error object from
        // their catch, exactly as V-Resume and V-Reference do — so `.rejected` almost never
        // fires and `.fulfilled` can carry a failure.
        const data = (action) => {
            const payload = action.payload && action.payload.responseData
            return payload && payload.status === 1 ? payload : null
        }

        builder.addCase(getVLearnSettings.fulfilled, (state, action) => {
            const d = data(action)
            if (!d) return
            state.settings = d.settings || state.settings
            state.allowedLessonTypes = d.allowedLessonTypes || VLEARN_LESSON_TYPES
            state.reach = d.reach || state.reach
        })

        builder.addCase(updateVLearnSettings.fulfilled, (state, action) => {
            const d = data(action)
            if (!d) return
            state.settings = d.settings || state.settings
            state.allowedLessonTypes = d.allowedLessonTypes || state.allowedLessonTypes
        })

        builder.addCase(listVLearnCategories.fulfilled, (state, action) => {
            const d = data(action)
            if (d) state.categories = d.list || []
        })

        builder.addCase(listVLearnCourses.pending, (state) => {
            state.catalogLoading = true
        })
        builder.addCase(listVLearnCourses.fulfilled, (state, action) => {
            state.catalogLoading = false
            const d = data(action)
            if (!d) return
            state.catalog = d.list || []
            state.catalogTotal = d.total || 0
            // Counted server-side over the whole result set, not the loaded page.
            state.catalogSummary = d.summary
                || { in_progress: 0, completed: 0, overdue: 0 }
            state.reach = d.reach || state.reach
        })
        builder.addCase(listVLearnCourses.rejected, (state) => {
            state.catalogLoading = false
        })

        // Both thunks hit the same endpoint and must fill the same state, but `fulfilled` is
        // matched per action type, so the loader-free twin needs its own case.
        const applyCourse = (state, action) => {
            const d = data(action)
            if (!d) return
            state.course = d.course || null
            state.sections = d.sections || []
            state.managers = d.managers || []
            state.access = d.access || initialState.access
            state.enrollment = d.enrollment || null
        }
        builder.addCase(getVLearnCourse.fulfilled, applyCourse)
        builder.addCase(refreshVLearnCourse.fulfilled, applyCourse)

        builder.addCase(getVLearnAudience.fulfilled, (state, action) => {
            const d = data(action)
            if (!d) return
            state.audienceLoaded = true
            state.audience = {
                target_type: d.target_type,
                teams: d.teams || [],
                collabs: d.collabs || [],
                enrolledCount: d.enrolledCount || 0
            }
        })

        builder.addCase(getVLearnAudienceTargets.fulfilled, (state, action) => {
            const d = data(action)
            if (!d) return
            state.audienceTargets = {
                teams: d.teams || [],
                collabs: d.collabs || [],
                canTargetAll: !!d.canTargetAll
            }
        })

        builder.addCase(getVLearnManagerCandidates.fulfilled, (state, action) => {
            const d = data(action)
            if (d) state.managerCandidates = d.list || []
        })

        builder.addCase(getVLearnDashboard.pending, (state) => {
            state.dashboardLoading = true
        })
        builder.addCase(getVLearnDashboard.fulfilled, (state, action) => {
            state.dashboardLoading = false
            const d = data(action)
            if (d) state.dashboardSummary = d.summary || null
        })
        builder.addCase(getVLearnDashboard.rejected, (state) => {
            state.dashboardLoading = false
        })

        builder.addCase(getVLearnLearners.fulfilled, (state, action) => {
            const d = data(action)
            if (!d) return
            state.dashboardLearners = d.list || []
            state.dashboardLearnersTotal = d.total || 0
        })

        builder.addCase(getVLearnLessonStats.fulfilled, (state, action) => {
            const d = data(action)
            if (d) state.dashboardLessons = d.list || []
        })

        builder.addCase(getVLearnActivity.fulfilled, (state, action) => {
            const d = data(action)
            if (d) state.dashboardActivity = d.list || []
        })

        builder.addCase(getVLearnCollabDetail.fulfilled, (state, action) => {
            const d = data(action)
            state.collabDetail = d || null
        })

        builder.addCase(listVLearnQuizzes.fulfilled, (state, action) => {
            const d = data(action)
            if (d) state.quizzes = d.list || []
        })

        builder.addCase(listVLearnQuestions.fulfilled, (state, action) => {
            const d = data(action)
            if (d) state.quizQuestions = d.list || []
        })

        builder.addCase(getVLearnQuizForLearner.fulfilled, (state, action) => {
            const d = data(action)
            state.quizForLearner = d || null
        })

        builder.addCase(startVLearnAttempt.fulfilled, (state, action) => {
            const d = data(action)
            if (!d) return
            state.attempt = d
            // A fresh attempt must not render under the previous one's result screen.
            state.attemptResult = null
        })

        builder.addCase(submitVLearnAttempt.fulfilled, (state, action) => {
            const d = data(action)
            if (!d) return
            state.attemptResult = d
            state.attempt = null
        })

        builder.addCase(getVLearnAttemptReview.fulfilled, (state, action) => {
            const d = data(action)
            state.attemptReview = d || null
        })

        builder.addCase(listVLearnMyAttempts.fulfilled, (state, action) => {
            const d = data(action)
            if (d) state.myAttempts = d.list || []
        })

        builder.addCase(listVLearnAnnouncements.fulfilled, (state, action) => {
            const d = data(action)
            if (d) state.announcements = d.list || []
        })

    }
})

/**
 * The shape every thunk in this file shares.
 *
 * Thunks NEVER reject — they return the error object from their catch, which is the
 * convention `vresumeSlice` and `vreferenceSlice` established and which every reducer above
 * is written against. `postRequest` resolves `{ responseData, body }` on success and
 * `{ responseError }` on failure, so a "successful" thunk can still carry a failure and the
 * reducers guard on `responseData.status === 1`.
 *
 * `withLoader` dispatches the global spinner around the call. Reserved for blocking
 * navigations — see the module comment.
 */
const call = (path, { withLoader = false } = {}) => async (body) => {
    try {
        if (withLoader) store.dispatch(setIsLoading(true))
        const response = await NetworkManager.getInstance().postRequest(path, body || {})
        if (withLoader) store.dispatch(setIsLoading(false))
        return response
    } catch (error) {
        if (withLoader) store.dispatch(setIsLoading(false))
        return error
    }
}

const P = config.apiPaths.vlearn

// ── Settings & categories ────────────────────────────────────────────────────────────

export const getVLearnSettings = createAsyncThunk("vlearn/settings/get", call(P.settingsGet))
export const updateVLearnSettings = createAsyncThunk("vlearn/settings/update", call(P.settingsUpdate))
export const listVLearnCategories = createAsyncThunk("vlearn/categories/list", call(P.categoriesList))
export const saveVLearnCategory = createAsyncThunk("vlearn/categories/save", call(P.categoriesSave))
export const deleteVLearnCategory = createAsyncThunk("vlearn/categories/delete", call(P.categoriesDelete))

// ── Courses ──────────────────────────────────────────────────────────────────────────

export const listVLearnCourses = createAsyncThunk("vlearn/courses/list", call(P.coursesList))
// Blocking: the player has nothing to render until the curriculum lands.
export const getVLearnCourse = createAsyncThunk("vlearn/courses/get", call(P.coursesGet, { withLoader: true }))

/**
 * The same read, WITHOUT the full-screen loader.
 *
 * The editor autosaves and then refreshes the course to pick up server-derived fields. Doing
 * that through `getVLearnCourse` threw the application-wide modal spinner over the screen
 * 700ms after every pause in typing — a full-viewport dimmed overlay that also swallowed any
 * click made during it. That directly contradicted this file's own rule at the top: the
 * editor autosaves, and a spinner on an autosave makes the module unusable.
 *
 * Same endpoint, same reducer (`getVLearnCourse.fulfilled` is keyed on the action type, so
 * this one needs its own case — see extraReducers). Use this for any refresh the user did not
 * explicitly ask to wait for; keep `getVLearnCourse` for the initial navigation.
 */
export const refreshVLearnCourse = createAsyncThunk("vlearn/courses/refresh", call(P.coursesGet))
export const saveVLearnCourse = createAsyncThunk("vlearn/courses/save", call(P.coursesSave))
export const publishVLearnCourse = createAsyncThunk("vlearn/courses/publish", call(P.coursesPublish, { withLoader: true }))
export const unpublishVLearnCourse = createAsyncThunk("vlearn/courses/unpublish", call(P.coursesUnpublish))
export const archiveVLearnCourse = createAsyncThunk("vlearn/courses/archive", call(P.coursesArchive))
export const deleteVLearnCourse = createAsyncThunk("vlearn/courses/delete", call(P.coursesDelete))
export const duplicateVLearnCourse = createAsyncThunk("vlearn/courses/duplicate", call(P.coursesDuplicate, { withLoader: true }))

// ── Audience & managers ──────────────────────────────────────────────────────────────

export const getVLearnAudienceTargets = createAsyncThunk("vlearn/audience/targets", call(P.audienceTargets))
export const getVLearnAudience = createAsyncThunk("vlearn/audience/get", call(P.audienceGet))
export const saveVLearnAudience = createAsyncThunk("vlearn/audience/save", call(P.audienceSave, { withLoader: true }))
export const refreshVLearnAudience = createAsyncThunk("vlearn/audience/refresh", call(P.audienceRefresh, { withLoader: true }))
export const getVLearnManagerCandidates = createAsyncThunk("vlearn/managers/candidates", call(P.managersCandidates))
export const addVLearnManager = createAsyncThunk("vlearn/managers/add", call(P.managersAdd))
export const removeVLearnManager = createAsyncThunk("vlearn/managers/remove", call(P.managersRemove))
export const transferVLearnOwnership = createAsyncThunk("vlearn/managers/transferOwnership", call(P.managersTransferOwnership))

// ── Curriculum ───────────────────────────────────────────────────────────────────────

export const saveVLearnSection = createAsyncThunk("vlearn/sections/save", call(P.sectionsSave))
export const deleteVLearnSection = createAsyncThunk("vlearn/sections/delete", call(P.sectionsDelete))
export const reorderVLearnSections = createAsyncThunk("vlearn/sections/reorder", call(P.sectionsReorder))
export const saveVLearnLesson = createAsyncThunk("vlearn/lessons/save", call(P.lessonsSave))
export const deleteVLearnLesson = createAsyncThunk("vlearn/lessons/delete", call(P.lessonsDelete))
export const reorderVLearnLessons = createAsyncThunk("vlearn/lessons/reorder", call(P.lessonsReorder))
export const moveVLearnLesson = createAsyncThunk("vlearn/lessons/move", call(P.lessonsMove))
export const deleteVLearnFile = createAsyncThunk("vlearn/files/delete", call(P.filesDelete))

/**
 * Uploads a lesson file.
 *
 * Not built with `call()` because it is multipart, not JSON: it goes through
 * `NetworkManager.uploadVLearnFile`, which posts a FormData and reports upload progress. A
 * training video is the one upload in this application large enough that a spinner is not an
 * acceptable answer, so the percentage is pushed into `state.uploadProgress` for a real bar.
 */
export const uploadVLearnFile = createAsyncThunk(
    "vlearn/files/upload",
    async ({ file, idLesson, kind }) => {
        try {
            store.dispatch(setUploadProgress(0))
            const data = await NetworkManager.getInstance().uploadVLearnFile(
                file, idLesson, kind, (percent) => store.dispatch(setUploadProgress(percent)))
            store.dispatch(setUploadProgress(null))
            return { responseData: data }
        } catch (error) {
            store.dispatch(setUploadProgress(null))
            return { responseError: error }
        }
    }
)

export const uploadVLearnCover = createAsyncThunk(
    "vlearn/courses/uploadCover",
    async ({ file, idCourse }) => {
        try {
            store.dispatch(setUploadProgress(0))
            const data = await NetworkManager.getInstance().uploadVLearnCover(
                file, idCourse, (percent) => store.dispatch(setUploadProgress(percent)))
            store.dispatch(setUploadProgress(null))
            return { responseData: data }
        } catch (error) {
            store.dispatch(setUploadProgress(null))
            return { responseError: error }
        }
    }
)

// ── Learner progress ─────────────────────────────────────────────────────────────────

export const openVLearnLesson = createAsyncThunk("vlearn/progress/openLesson", call(P.openLesson))
/**
 * The video/reading heartbeat. Fires every few seconds while a lesson is open, so it takes
 * no loader and has no reducer — the server's accumulated total is authoritative and the
 * player has nothing to do with the response.
 */
export const vlearnHeartbeat = createAsyncThunk("vlearn/progress/heartbeat", call(P.heartbeat))
export const completeVLearnLesson = createAsyncThunk("vlearn/progress/completeLesson", call(P.completeLesson))

// ── Teacher dashboard ────────────────────────────────────────────────────────────────

export const getVLearnDashboard = createAsyncThunk("vlearn/dashboard/course", call(P.dashboardCourse))
export const getVLearnLearners = createAsyncThunk("vlearn/dashboard/learners", call(P.dashboardLearners))
export const getVLearnLessonStats = createAsyncThunk("vlearn/dashboard/lessons", call(P.dashboardLessons))
export const getVLearnActivity = createAsyncThunk("vlearn/dashboard/activity", call(P.dashboardActivity))
export const getVLearnCollabDetail = createAsyncThunk("vlearn/dashboard/collab", call(P.dashboardCollab))

/**
 * Downloads the dashboard workbook.
 *
 * Goes through `fetchFile`, not `postRequest`: the endpoint answers an Excel stream, and
 * `postRequest` would try to parse those bytes as JSON.
 */
export const exportVLearnDashboard = createAsyncThunk(
    "vlearn/dashboard/export",
    async ({ idCourse, filename }) => {
        try {
            store.dispatch(setIsLoading(true))
            await NetworkManager.getInstance().fetchFile(
                P.dashboardExport, { id_course: idCourse }, filename)
            store.dispatch(setIsLoading(false))
            return { responseData: { status: 1 } }
        } catch (error) {
            store.dispatch(setIsLoading(false))
            return { responseError: error }
        }
    }
)

/** Same reasoning as the export above — a PDF stream, so `fetchFile`, never `postRequest`. */
export const downloadVLearnCertificate = createAsyncThunk(
    "vlearn/certificate/download",
    async ({ idCourse, idCollab, filename }) => {
        try {
            store.dispatch(setIsLoading(true))
            const body = idCollab ? { id_course: idCourse, id_collab: idCollab } : { id_course: idCourse }
            await NetworkManager.getInstance().fetchFile(P.certificateDownload, body, filename)
            store.dispatch(setIsLoading(false))
            return { responseData: { status: 1 } }
        } catch (error) {
            store.dispatch(setIsLoading(false))
            return { responseError: error }
        }
    }
)

// ── Announcements ────────────────────────────────────────────────────────────────────

export const listVLearnAnnouncements = createAsyncThunk("vlearn/announcements/list", call(P.announcementsList))
export const saveVLearnAnnouncement = createAsyncThunk("vlearn/announcements/save", call(P.announcementsSave))
export const deleteVLearnAnnouncement = createAsyncThunk("vlearn/announcements/delete", call(P.announcementsDelete))
export const markVLearnAnnouncementRead = createAsyncThunk("vlearn/announcements/markRead", call(P.announcementsMarkRead))

// ── Quizzes ──────────────────────────────────────────────────────────────────────────

export const listVLearnQuizzes = createAsyncThunk("vlearn/quizzes/list", call(P.quizzesList))
export const saveVLearnQuiz = createAsyncThunk("vlearn/quizzes/save", call(P.quizzesSave))
export const deleteVLearnQuiz = createAsyncThunk("vlearn/quizzes/delete", call(P.quizzesDelete))
export const listVLearnQuestions = createAsyncThunk("vlearn/questions/list", call(P.questionsList))
export const saveVLearnQuestion = createAsyncThunk("vlearn/questions/save", call(P.questionsSave))
export const deleteVLearnQuestion = createAsyncThunk("vlearn/questions/delete", call(P.questionsDelete))
export const reorderVLearnQuestions = createAsyncThunk("vlearn/questions/reorder", call(P.questionsReorder))
export const importVLearnQuestions = createAsyncThunk("vlearn/questions/import", call(P.questionsImport))

export const getVLearnQuizForLearner = createAsyncThunk("vlearn/quizzes/getForLearner", call(P.quizForLearner))
// Blocking on purpose: starting an attempt records one server-side, and a second click
// while the first is in flight must not be possible.
export const startVLearnAttempt = createAsyncThunk("vlearn/attempt/start", call(P.attemptStart, { withLoader: true }))
export const submitVLearnAttempt = createAsyncThunk("vlearn/attempt/submit", call(P.attemptSubmit, { withLoader: true }))
export const getVLearnAttemptReview = createAsyncThunk("vlearn/attempt/review", call(P.attemptReview))
export const listVLearnMyAttempts = createAsyncThunk("vlearn/attempt/list", call(P.attemptList))

export const {
    closeCourse, setCurrentLesson, patchLessonProgressLocal,
    reorderSectionsLocal, reorderLessonsLocal,
    setSaveState, setUploadProgress, clearAttempt
} = VLearnSlice.actions

export default VLearnSlice.reducer
